/**
 * API Configuration
 *
 * Handles API base URL for different environments:
 * - Production/Staging: Auto-detect from window.location (works with HTTP and HTTPS)
 * - Development: localhost:4001 (direct backend connection)
 * - SSR: Use NEXT_PUBLIC_API_URL or empty string for relative URLs
 *
 * IMPORTANT: Use nullish coalescing (??) not logical OR (||)
 * because empty string is a valid value for production relative URLs.
 */

// Check if we're in the browser
const isBrowser = typeof window !== 'undefined';

// Check if NEXT_PUBLIC_API_URL is explicitly set (including empty string)
// Empty string means "use relative URLs" (for production via Caddy)
// undefined/null means "use development fallback"
const envApiUrl = process.env.NEXT_PUBLIC_API_URL;

/**
 * Get the API base URL, auto-detecting protocol in browser
 * This allows one build to work on both HTTP (dev) and HTTPS (staging/prod)
 */
function computeApiBaseUrl(): string {
  // If env var is explicitly set (including empty string), use it
  if (envApiUrl !== undefined && envApiUrl !== null) {
    // If empty string, use relative URLs (Caddy proxy)
    if (envApiUrl === '') {
      return '';
    }
    // If it's a relative URL or already protocol-relative, use as-is
    if (envApiUrl.startsWith('/') || envApiUrl.startsWith('//')) {
      return envApiUrl;
    }
    // If in browser and env URL has explicit http/https, adapt to current protocol
    if (isBrowser && (envApiUrl.startsWith('http://') || envApiUrl.startsWith('https://'))) {
      const url = new URL(envApiUrl);
      // Match the current page's protocol to avoid mixed content
      url.protocol = window.location.protocol;
      return url.toString().replace(/\/$/, ''); // Remove trailing slash
    }
    return envApiUrl;
  }

  // Default for development
  return 'http://localhost:4001';
}

export const API_BASE_URL = computeApiBaseUrl();

// For debugging - shows what URL is being used
export const getApiBaseUrl = (): string => API_BASE_URL;

// Helper to check if using relative URLs (production mode)
export const isRelativeApiUrl = (): boolean => API_BASE_URL === '';
